import React, { useMemo, useState } from 'react';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { Button } from '@/components/UI/Button';
import { Input } from '@/components/UI/Input';
import { Select } from '@/components/UI/Select';
import { Modal } from '@/components/UI/Modal';
import { Alert } from '@/components/UI/Alert';
import { Badge } from '@/components/UI/Badge';
import { LoadingState } from '@/components/UI/LoadingState';
import { EmptyState } from '@/components/UI/EmptyState';
import { getErrorDetail } from '@/utils/apiError';
import {
  useListLlmPricesQuery,
  useAddLlmPriceMutation,
  useDeleteLlmPriceMutation,
  type LlmPrice,
} from '@/redux/internal-services/platformAdminApi';

const RATES: { key: keyof LlmPrice; label: string }[] = [
  { key: 'input_per_mtok', label: 'Input' },
  { key: 'cached_input_per_mtok', label: 'Cached in' },
  { key: 'output_per_mtok', label: 'Output' },
  { key: 'cache_write_per_mtok', label: 'Cache write' },
  { key: 'audio_input_per_mtok', label: 'Audio in' },
  { key: 'cached_audio_input_per_mtok', label: 'Cached audio in' },
  { key: 'image_input_per_mtok', label: 'Image in' },
  { key: 'audio_output_per_mtok', label: 'Audio out' },
  { key: 'per_audio_second', label: '$/audio sec' },
];

const today = () => new Date().toISOString().slice(0, 10);
const money = (v?: number | null) => (v === null || v === undefined ? '—' : `$${v}`);

interface Draft { vendor: string; model: string; effective_from: string; note: string; rates: Record<string, string> }
const blank = (vendor = 'openai', model = ''): Draft => ({ vendor, model, effective_from: today(), note: '', rates: {} });

const PricingPage: React.FC = () => {
  const { data, isLoading, isError, error } = useListLlmPricesQuery();
  const [addPrice, { isLoading: saving }] = useAddLlmPriceMutation();
  const [deletePrice] = useDeleteLlmPriceMutation();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const prices = useMemo(() => data?.data.prices ?? [], [data]);
  const unpriced = data?.data.unpriced_models ?? [];
  const vendors = data?.data.vendors ?? ['openai', 'anthropic', 'xai', 'google'];
  const groups = useMemo(() => {
    const m = new Map<string, LlmPrice[]>();
    for (const p of prices) m.set(`${p.vendor}::${p.model}`, [...(m.get(`${p.vendor}::${p.model}`) ?? []), p]);
    return [...m.entries()];
  }, [prices]);
  const nowDay = today();

  const save = async () => {
    if (!draft) return;
    setFormError(null);
    const num = (k: string) => (draft.rates[k] === undefined || draft.rates[k] === '' ? null : Number(draft.rates[k]));
    if (!draft.model.trim() || num('input_per_mtok') === null || num('output_per_mtok') === null) {
      setFormError('Model, input rate and output rate are required (use 0 if the model is not billed per token).');
      return;
    }
    try {
      await addPrice({
        vendor: draft.vendor, model: draft.model.trim(), effective_from: draft.effective_from, note: draft.note || null,
        input_per_mtok: num('input_per_mtok')!, output_per_mtok: num('output_per_mtok')!,
        cached_input_per_mtok: num('cached_input_per_mtok'), cache_write_per_mtok: num('cache_write_per_mtok'),
        audio_input_per_mtok: num('audio_input_per_mtok'), cached_audio_input_per_mtok: num('cached_audio_input_per_mtok'),
        image_input_per_mtok: num('image_input_per_mtok'), audio_output_per_mtok: num('audio_output_per_mtok'),
        per_audio_second: num('per_audio_second'),
      }).unwrap();
      setDraft(null);
    } catch (e) {
      setFormError(getErrorDetail(e) ?? 'Could not save the price.');
    }
  };

  return (
    <PageShell
      maxWidth="max-w-6xl"
      header={
        <PageHeader
          title="Pricing"
          subtitle="USD per 1M tokens, by effective date. Each run is estimated with the version in force on its day; add a new version when a vendor changes a price."
          actions={<Button size="sm" onClick={() => setDraft(blank())}>Add price version</Button>}
        />
      }
    >
      {isError && <Alert tone="danger">{getErrorDetail(error) ?? 'Could not load prices.'}</Alert>}
      {unpriced.length > 0 && (
        <Alert tone="warning" className="mb-4">
          Used but not priced (their cost shows as unpriced on the Usage page):{' '}
          {unpriced.map((u, i) => (
            <button key={`${u.vendor}${u.model}`} type="button" className="font-semibold underline"
              onClick={() => setDraft(blank(u.vendor, u.model))}>
              {i > 0 ? ', ' : ''}{u.vendor}/{u.model} ({u.runs} runs)
            </button>
          ))}
        </Alert>
      )}
      {isLoading ? <LoadingState label="Loading prices…" className="py-16" /> : groups.length === 0 ? (
        <EmptyState className="rounded-card border border-dashed border-ui-border py-12" title="No prices yet"
          description="Add a price version to start estimating cost." />
      ) : (
        <div className="table-card">
          <div className="no-scrollbar overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr>
                  <th className="th-cell">Vendor / model</th>
                  <th className="th-cell">Effective from</th>
                  {RATES.map((r) => <th key={r.key} className="th-cell text-right">{r.label}</th>)}
                  <th className="th-cell" />
                </tr>
              </thead>
              <tbody>
                {groups.map(([key, versions]) => {
                  const current = versions.find((v) => v.effective_from <= nowDay)?.price_id;   // versions are newest first
                  return versions.map((v, i) => (
                    <tr key={v.price_id} className="tr-hover">
                      <td className="td-cell">
                        {i === 0 && <p className="text-sm font-medium text-ui-text">{v.model}</p>}
                        {i === 0 && <p className="text-caption text-ui-text3">{v.vendor}</p>}
                      </td>
                      <td className="td-cell whitespace-nowrap text-xs">
                        {v.effective_from}{' '}
                        {v.price_id === current && <Badge variant="success">current</Badge>}
                        {v.effective_from > nowDay && <Badge variant="accent">scheduled</Badge>}
                        {v.note && <p className="text-caption text-ui-text3" title={v.note}>{v.note.slice(0, 48)}</p>}
                      </td>
                      {RATES.map((r) => (
                        <td key={r.key} className="td-cell text-right font-mono text-xs">{money(v[r.key] as number | null)}</td>
                      ))}
                      <td className="td-cell text-right">
                        <Button variant="link" size="xs" onClick={() => setDraft({
                          vendor: v.vendor, model: v.model, effective_from: today(), note: '',
                          rates: Object.fromEntries(RATES.filter((r) => v[r.key] !== null && v[r.key] !== undefined).map((r) => [r.key, String(v[r.key])])),
                        })}>New version</Button>
                        <Button variant="destructive-ghost" size="xs" aria-label={`Delete ${key} ${v.effective_from}`}
                          onClick={() => { if (window.confirm(`Delete the ${v.effective_from} version of ${v.model}?`)) void deletePrice(v.price_id); }}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <p className="mt-3 text-caption text-ui-text3">
        Estimates only, from the token counts each provider reported. OpenAI/xAI/Gemini cached tokens are billed at the cached rate inside
        input; Anthropic cache reads/writes are billed on top. Voice audio uses the audio rates when set. Long-context surcharges are not modelled.
      </p>

      {draft && (
        <Modal title="Price version" onClose={() => setDraft(null)} onSave={save} saving={saving} saveLabel="Save version" closeOnBackdrop={false}>
          <div className="space-y-3">
            {formError && <Alert tone="danger">{formError}</Alert>}
            <div className="grid grid-cols-2 gap-3">
              <Select label="Vendor" value={draft.vendor} onChange={(e) => setDraft({ ...draft, vendor: e.target.value })}>
                {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
              </Select>
              <Input label="Effective from" type="date" value={draft.effective_from}
                onChange={(e) => setDraft({ ...draft, effective_from: e.target.value })} />
            </div>
            <Input label="Model id (exactly as recorded)" value={draft.model} onChange={(e) => setDraft({ ...draft, model: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              {RATES.map((r) => (
                <Input key={r.key} label={`${r.label}${r.key === 'per_audio_second' ? '' : ' / 1M tok'}`} type="number" min="0" step="any"
                  required={r.key === 'input_per_mtok' || r.key === 'output_per_mtok'}
                  value={draft.rates[r.key] ?? ''} onChange={(e) => setDraft({ ...draft, rates: { ...draft.rates, [r.key]: e.target.value } })} />
              ))}
            </div>
            <Input label="Note (optional)" value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
          </div>
        </Modal>
      )}
    </PageShell>
  );
};

export default PricingPage;
